import styled from 'styled-components';
import { colors } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import {
  AiIconGradientAnimated,
  AI_ICON_ANIMATED_CSS_VARIABLE,
} from '@ardoq/ai';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  CompletenessBand,
  isCompleteScore,
} from './applicationCompletenessScore';
import { Tooltip, useDelayedHover } from '@ardoq/popovers';
import { type KeyboardEvent, useRef } from 'react';

const BAND_FILL_COLOR: Record<CompletenessBand, string> = {
  low: colors.iconError,
  medium: colors.iconWarning,
  high: colors.iconSuccess,
};

const SCORE_ARIA_LABEL: Record<
  CompletenessBand,
  { key: string; fallback: string }
> = {
  low: {
    key: 'inventory:cellRenderers.completeness.scoreAriaLabel.low',
    fallback: 'Low completeness score, {{score}} out of 100',
  },
  medium: {
    key: 'inventory:cellRenderers.completeness.scoreAriaLabel.medium',
    fallback: 'Medium completeness score, {{score}} out of 100',
  },
  high: {
    key: 'inventory:cellRenderers.completeness.scoreAriaLabel.high',
    fallback: 'High completeness score, {{score}} out of 100',
  },
};

const getScoreAriaLabel = (
  i18nLocale: I18nLocale,
  band: CompletenessBand,
  score: number
): string => {
  const { key, fallback } = SCORE_ARIA_LABEL[band];
  return t(i18nLocale, key, { score }, fallback);
};

export const getScoreTagTooltipLabel = (
  i18nLocale: I18nLocale,
  score: number
): string =>
  isCompleteScore(score)
    ? t(
        i18nLocale,
        'inventory:cellRenderers.completeness.planNextStepsTooltip',
        'Plan next steps with AI'
      )
    : t(
        i18nLocale,
        'inventory:cellRenderers.completeness.improveRatingTooltip',
        'Improve rating with AI'
      );

const isActivationKey = (key: string): boolean =>
  key === 'Enter' || key === ' ';

const handleActivationKeyDown =
  (onActivate: VoidFunction) =>
  (event: KeyboardEvent<HTMLSpanElement>): void => {
    if (!isActivationKey(event.key)) return;
    event.preventDefault();
    onActivate();
  };

const GAUGE_SIZE = 16;
const GAUGE_RING_WIDTH = 1.5;
const GAUGE_WEDGE_INSET = 2;

const Gauge = styled.div<{ $fillColor: string }>`
  position: relative;
  box-sizing: border-box;
  width: ${GAUGE_SIZE}px;
  height: ${GAUGE_SIZE}px;
  border-radius: 50%;
  border: ${GAUGE_RING_WIDTH}px solid ${({ $fillColor }) => $fillColor};
`;

const GaugeWedge = styled.div<{ $percentage: number; $fillColor: string }>`
  position: absolute;
  inset: ${GAUGE_WEDGE_INSET}px;
  border-radius: 50%;
  background: conic-gradient(
    ${({ $fillColor }) => $fillColor} ${({ $percentage }) => $percentage}%,
      transparent 0
  );
`;

const Score = styled.span`
  text-decoration: underline;
  text-decoration-color: ${colors.grey500};
`;

const InteractiveScore = styled(Score)`
  cursor: pointer;
`;

const HoverableAiIcon = styled.div`
  display: inline-flex;
  align-items: center;
  opacity: 0;
  pointer-events: none;
  cursor: pointer;

  .ag-row-hover & {
    opacity: 1;
    pointer-events: auto;
    ${AI_ICON_ANIMATED_CSS_VARIABLE}: 1;
  }
`;

type ApplicationCompletenessScoreTagProps = {
  score: number;
  band: CompletenessBand;
  i18nLocale: I18nLocale;
};

export const ApplicationCompletenessScoreTag = ({
  score,
  band,
  i18nLocale,
}: ApplicationCompletenessScoreTagProps) => {
  const fillColor = BAND_FILL_COLOR[band];
  return (
    <FlexBox align="center" gap="xsmall">
      <Gauge $fillColor={fillColor}>
        <GaugeWedge $percentage={score} $fillColor={fillColor} />
      </Gauge>
      <Score
        tabIndex={0}
        aria-label={getScoreAriaLabel(i18nLocale, band, score)}
      >
        {score}
      </Score>
    </FlexBox>
  );
};

type ApplicationCompletenessInteractiveScoreTagProps =
  ApplicationCompletenessScoreTagProps & {
    onAskAI: VoidFunction;
  };

export const ApplicationCompletenessInteractiveScoreTag = ({
  score,
  band,
  i18nLocale,
  onAskAI,
}: ApplicationCompletenessInteractiveScoreTagProps) => {
  const fillColor = BAND_FILL_COLOR[band];
  const scoreRef = useRef<HTMLSpanElement>(null);
  const { isOpen, handleMouseEnter, handleMouseLeave, handleClose } =
    useDelayedHover();
  return (
    <FlexBox align="center" gap="xsmall">
      <Gauge $fillColor={fillColor}>
        <GaugeWedge $percentage={score} $fillColor={fillColor} />
      </Gauge>
      <InteractiveScore
        ref={scoreRef}
        tabIndex={0}
        role="button"
        aria-label={getScoreAriaLabel(i18nLocale, band, score)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        onClick={onAskAI}
        onKeyDown={handleActivationKeyDown(onAskAI)}
      >
        {score}
      </InteractiveScore>
      <HoverableAiIcon
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onAskAI}
      >
        <AiIconGradientAnimated />
      </HoverableAiIcon>
      <Tooltip isOpen={isOpen} onClose={handleClose} anchorRef={scoreRef}>
        {getScoreTagTooltipLabel(i18nLocale, score)}
      </Tooltip>
    </FlexBox>
  );
};
